// src/hooks/useWithSound.js
import { useRef, useEffect, useState } from "react";

export const useWithSound = (audioSource) => {
    const soundRef = useRef(null);
    const [ready, setReady] = useState(false);
    const unlockedRef = useRef(false);
    const lastPlayedRef = useRef(0);

    // load audio
    useEffect(() => {
        if (!audioSource) return;

        console.log('[useWithSound] loading audio from:', audioSource);
        const audio = new Audio(audioSource);
        audio.preload = 'auto';

        const onReady = () => {
            console.log('[useWithSound] audio ready ✅');
            setReady(true);
        };

        audio.addEventListener('canplaythrough', onReady);
        audio.addEventListener('loadeddata', onReady);
        audio.addEventListener('loadedmetadata', onReady);
        audio.addEventListener('error', (e) => console.error('[useWithSound] ❌ load error', e));

        soundRef.current = audio;

        const fallbackTimer = setTimeout(() => {
            console.log('[useWithSound] fallback ready ✅');
            setReady(true);
        }, 2000);

        return () => {
            audio.removeEventListener('canplaythrough', onReady);
            audio.removeEventListener('loadeddata', onReady);
            audio.removeEventListener('loadedmetadata', onReady);
            clearTimeout(fallbackTimer);
            audio.pause();
            audio.src = '';
            setReady(false);
        };
    }, [audioSource]);

    // ✅ unlock runs AFTER ready is true — soundRef.current is guaranteed to exist
    useEffect(() => {
        if (!ready) return; // ← wait until audio is loaded first

        console.log('[useWithSound] registering unlock listeners ✅');

        const unlock = async () => {
            console.log('[unlock] triggered, already unlocked:', unlockedRef.current);
            if (unlockedRef.current) return;

            try {
                soundRef.current.volume = 0;
                await soundRef.current.play();
                soundRef.current.pause();
                soundRef.current.currentTime = 0;
                soundRef.current.volume = 1;
                unlockedRef.current = true;
                console.log('[useWithSound] audio unlocked ✅');
            } catch (e) {
                console.error('[unlock] ❌ failed:', e);
            }
        };

        ['click', 'keydown', 'touchstart', 'scroll'].forEach(e =>
            document.addEventListener(e, unlock, { once: true, passive: true })
        );

        return () => {
            ['click', 'keydown', 'touchstart', 'scroll'].forEach(e =>
                document.removeEventListener(e, unlock)
            );
        };
    }, [ready]); // ← depends on ready, not []

    const playSound = async () => {
        console.log('[playSound] called — ready:', ready, '| unlocked:', unlockedRef.current);

        if (!soundRef.current) { console.warn('[playSound] ❌ no audio instance'); return; }
        if (!ready) { console.warn('[playSound] ❌ not ready'); return; }
        if (!unlockedRef.current) { console.warn('[playSound] ❌ not unlocked'); return; }

        const now = Date.now();
        if (now - lastPlayedRef.current < 1000) { console.warn('[playSound] ❌ throttled'); return; }
        lastPlayedRef.current = now;

        try {
            soundRef.current.currentTime = 0;
            await soundRef.current.play();
            console.log('[playSound] ✅ played successfully');
        } catch (err) {
            console.error('[playSound] ❌ error:', err);
        }
    };

    const pauseSound = () => soundRef.current?.pause();

    return { playSound, pauseSound, ready };
};